import { IconButton } from '@creditornot/cb-button';
import { CaretDown, Cross } from '@creditornot/cb-icons';
import { colors, cssThemedValue, typographyCss } from '@creditornot/cb-ingredients';
import { MenuItemGroup } from '@creditornot/cb-menu';
import { Popover, PopoverPlacement as Placement } from '@creditornot/cb-popover';
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';

import HierarchyOptions, { Option } from './HierarchyOptions';
import useElementDimension from './useElementDimension';

export type MultiLevelSelectProps = {
  caretPosition?: 'left' | 'right';
  value: string | null;
  placeholder?: string;
  menuPlacement?: Placement;
  className?: string;
  invalid?: boolean;
  disabled?: boolean;
  options: Option[];
  onClearClick?: React.MouseEventHandler<HTMLButtonElement>;
  onChange: (value: string) => void;
};

const Root = styled(Popover)<{ disabled: boolean }>`
  width: 100%;
  min-width: 0;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const InputContainer = styled.label<{ disabled: boolean; invalid: boolean }>`
  display: flex;
  align-items: center;
  height: 36px;
  border-radius: 8px;
  border-width: 1px;
  border-style: solid;
  padding: 4px 12px;
  width: 100%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${({ disabled }) => (disabled ? cssThemedValue('textSecondary') : cssThemedValue('textPrimary'))};
  border-color: ${({ invalid, disabled }) =>
    disabled
      ? cssThemedValue({
          dark: colors.salt12,
          light: colors.pepper12,
        })
      : invalid
        ? colors.strawberry100
        : cssThemedValue('borderPrimary')};
  background-color: ${({ disabled }) =>
    disabled &&
    cssThemedValue({
      light: colors.pepper8,
      dark: colors.salt8,
    })};

  :focus-within {
    border-color: ${({ invalid }) => (invalid ? colors.strawberry100 : colors.wolt100)};
  }
`;

const StyledIconButton = styled(IconButton)`
  padding-inline-start: 8px;
`;

const Input = styled.input<{ disabled?: boolean }>`
  ${typographyCss.Body3};
  font-weight: 400;
  width: 100%;
  background: transparent;
  outline: none;
  border: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  color: ${cssThemedValue('textPrimary')};
`;

const Content = styled(MenuItemGroup)<{ width?: number }>`
  padding: 8px 16px;
  max-height: 332px;
  overflow-y: auto;
  width: ${({ width }) => (width ? `${width}px` : 'auto')};
`;

const StyledCaretDown = styled(CaretDown)<{ disabled: boolean }>`
  padding-inline-start: 8px;
  min-width: 24px;
  height: 12px;
  fill: ${({ disabled }) => (disabled ? cssThemedValue('textTertiary') : cssThemedValue('textSecondary'))};
`;

const DisplayValue = styled.div`
  ${typographyCss.Body3};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-inline-start: -4px;
  width: 100%;
`;

const MultiLevelSelect = ({
  caretPosition = 'left',
  placeholder,
  menuPlacement = 'bottom',
  invalid = false,
  disabled = false,
  value,
  className,
  onClearClick,
  onChange,
  options,
}: MultiLevelSelectProps) => {
  const [show, setShow] = useState(false);
  const [search, setSearch] = useState<string>('');
  const { dimension, ref } = useElementDimension<HTMLDivElement>();
  const isSearching = search !== '';

  const displayValue = useMemo(() => options.find(op => op.value === value)?.label ?? '', [options, value]);

  const handleClickOutside = useCallback(() => {
    setShow(false);
    setTimeout(() => setSearch(''), 100);
  }, []);

  const handleInputFocus = useCallback(() => {
    setShow(true);
  }, []);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
  }, []);

  const handleClearClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setSearch('');
      onClearClick?.(e);
    },
    [onClearClick],
  );

  const handleChange = useCallback(
    (newValue: string) => {
      setShow(false);
      setTimeout(() => {
        setSearch('');
        onChange(newValue);
      }, 100);
    },
    [onChange],
  );

  return (
    <Root
      className={className}
      renderArrow={false}
      disableContentPadding
      show={show}
      showOnHover={false}
      disabled={disabled}
      onClickOutside={handleClickOutside}
      placement={menuPlacement}
      // Ensure dropdown stay on top of wolt-modal
      contentZIndex={999999}
      content={
        <Content width={dimension?.width}>
          <HierarchyOptions
            values={value === null ? [] : [value]}
            onChange={handleChange}
            search={search}
            options={options}
            caretPosition={caretPosition}
          />
        </Content>
      }
    >
      <div ref={ref}>
        <InputContainer invalid={invalid} disabled={disabled}>
          <Input
            disabled={disabled}
            placeholder={placeholder}
            value={search ?? ''}
            style={{
              width: value && !isSearching ? '5px' : '100%',
            }}
            onFocus={handleInputFocus}
            onChange={handleInputChange}
          />
          {value && !isSearching && <DisplayValue title={displayValue}>{displayValue}</DisplayValue>}
          {onClearClick && (
            <StyledIconButton
              disabled={disabled}
              onMouseDown={handleMouseDown}
              onClick={handleClearClick}
              background="animated"
              variant="grey"
              size="tiny"
            >
              <Cross />
            </StyledIconButton>
          )}
          <StyledCaretDown disabled={disabled} />
        </InputContainer>
      </div>
    </Root>
  );
};

export default MultiLevelSelect;
